/**
 * Wave 5 — Negotiation rounds + side-by-side comparison.
 */
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { formatMoney, formatPercent } from '@/utils/format';
import {
  compareNegotiationRounds,
  pickNegotiationComparePair,
} from './clarificationAddendaNegotiationUx';

const { Text, Paragraph, Title } = Typography;
const { TextArea } = Input;

export type EpcNegotiationPanelProps = {
  tenderId: string;
  currency?: string;
  rows: Record<string, unknown>[];
  onUpdated?: () => void;
};

export const EpcNegotiationPanel: React.FC<EpcNegotiationPanelProps> = ({
  tenderId,
  currency = 'AED',
  rows,
  onUpdated,
}) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const defaultPair = useMemo(() => pickNegotiationComparePair(rows), [rows]);
  const [leftId, setLeftId] = useState<string | null>(null);
  const [rightId, setRightId] = useState<string | null>(null);

  const left =
    rows.find((r) => String(r.id) === leftId) ||
    defaultPair?.left ||
    null;
  const right =
    rows.find((r) => String(r.id) === rightId) ||
    defaultPair?.right ||
    null;

  const compare = useMemo(() => {
    if (!left || !right) return null;
    return compareNegotiationRounds(left, right);
  }, [left, right]);

  const createM = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcCommercialService.createNegotiation(values),
    onSuccess: () => {
      message.success('Negotiation round recorded');
      setOpen(false);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', tenderId] });
      onUpdated?.();
    },
    onError: (e: Error) => message.error(e.message || 'Create failed'),
  });

  const roundOptions = rows.map((r) => ({
    value: String(r.id),
    label: `R${r.round_number || '?'} — ${r.title || 'Round'} (${r.offered_value != null ? formatMoney(Number(r.offered_value), { currency: String(r.currency || currency) }) : '—'})`,
  }));

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Card
        size="small"
        title="Negotiation rounds"
        extra={
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setOpen((v) => !v)}>
            New round
          </Button>
        }
      >
        <Paragraph type="secondary" style={{ marginTop: 0 }}>
          Append-only rounds via domain API. Compare last vs previous (or pick any two).
        </Paragraph>
        <Table
          size="small"
          rowKey={(r) => String(r.id)}
          dataSource={rows}
          pagination={false}
          scroll={{ x: 720 }}
          locale={{ emptyText: 'No negotiation rounds yet' }}
          columns={[
            { title: 'Round', dataIndex: 'round_number', width: 80 },
            { title: 'Title', dataIndex: 'title' },
            {
              title: 'Offered value',
              dataIndex: 'offered_value',
              width: 140,
              render: (v, r) =>
                v != null
                  ? formatMoney(Number(v), { currency: String(r.currency || currency) })
                  : '—',
            },
            {
              title: 'Outcome',
              dataIndex: 'outcome',
              width: 120,
              render: (v) => (v ? <Tag>{String(v)}</Tag> : '—'),
            },
            {
              title: 'Notes',
              dataIndex: 'notes',
              ellipsis: true,
              render: (v) => v || '—',
            },
          ]}
        />
      </Card>

      <Card size="small" title="Round comparison">
        {rows.length < 2 ? (
          <Alert
            type="info"
            showIcon
            message="Need at least two rounds to compare"
          />
        ) : (
          <>
            <Space wrap style={{ marginBottom: 12 }}>
              <Select
                style={{ minWidth: 220 }}
                placeholder="Left round"
                options={roundOptions}
                value={left ? String(left.id) : undefined}
                onChange={setLeftId}
              />
              <Text type="secondary">vs</Text>
              <Select
                style={{ minWidth: 220 }}
                placeholder="Right round"
                options={roundOptions}
                value={right ? String(right.id) : undefined}
                onChange={setRightId}
              />
            </Space>
            {compare ? (
              <>
                <Title level={5} style={{ marginTop: 0 }}>
                  Round {compare.leftRound} → {compare.rightRound}
                </Title>
                <Descriptions size="small" bordered column={{ xs: 1, sm: 2 }}>
                  <Descriptions.Item label="Left value">
                    {compare.leftValue != null
                      ? formatMoney(compare.leftValue, { currency })
                      : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Right value">
                    {compare.rightValue != null
                      ? formatMoney(compare.rightValue, { currency })
                      : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Value delta">
                    {compare.valueDelta != null
                      ? formatMoney(compare.valueDelta, { currency })
                      : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Delta %">
                    {compare.valueDeltaPct != null
                      ? formatPercent(compare.valueDeltaPct)
                      : '— (needs both values)'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Outcome">
                    {compare.leftOutcome} → {compare.rightOutcome}{' '}
                    {compare.outcomeChanged ? (
                      <Tag color="warning">Changed</Tag>
                    ) : (
                      <Tag>Same</Tag>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Notes">
                    {compare.notesChanged ? (
                      <Tag color="warning">Changed</Tag>
                    ) : (
                      <Tag>Same</Tag>
                    )}
                  </Descriptions.Item>
                </Descriptions>
                {compare.termDiffs.length ? (
                  <Table
                    style={{ marginTop: 12 }}
                    size="small"
                    rowKey={(r) => r.key}
                    pagination={false}
                    dataSource={compare.termDiffs}
                    columns={[
                      { title: 'Term', dataIndex: 'key', width: 160 },
                      { title: `R${compare.leftRound}`, dataIndex: 'left' },
                      { title: `R${compare.rightRound}`, dataIndex: 'right' },
                    ]}
                  />
                ) : (
                  <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
                    No term field differences in `terms_json`.
                  </Text>
                )}
              </>
            ) : null}
          </>
        )}
      </Card>

      {open ? (
        <Card size="small" title="New negotiation round">
        <Form
          form={form}
          layout="vertical"
          initialValues={{ currency }}
          onFinish={(values) => {
            let termsJson: unknown;
            if (values.termsText) {
              try {
                termsJson = JSON.parse(values.termsText);
              } catch {
                termsJson = { notes: values.termsText };
              }
            }
            createM.mutate({
              tenderId,
              title: values.title,
              offeredValue: values.offeredValue,
              currency: values.currency || currency,
              outcome: values.outcome,
              notes: values.notes,
              termsJson,
            });
          }}
        >
          <Form.Item name="title" label="Title">
            <Input placeholder="Round title" />
          </Form.Item>
          <Form.Item name="offeredValue" label="Offered value">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="currency" label="Currency">
            <Input />
          </Form.Item>
          <Form.Item name="outcome" label="Outcome">
            <Select
              allowClear
              options={[
                { value: 'OPEN', label: 'Open' },
                { value: 'COUNTER', label: 'Counter' },
                { value: 'ACCEPTED', label: 'Accepted' },
                { value: 'REJECTED', label: 'Rejected' },
              ]}
            />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name="termsText"
            label="Terms JSON (optional)"
            extra='Object e.g. {"payment":"30/70","retention":"5%"}'
          >
            <TextArea rows={3} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={createM.isPending}>
              Save
            </Button>
            <Button
              onClick={() => {
                setOpen(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
          </Space>
        </Form>
        </Card>
      ) : null}
    </Space>
  );
};

export default EpcNegotiationPanel;
